"use client";

import { UserPlus } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { type CSSProperties, type ReactNode, useId } from "react";
import { cn } from "@/lib/utils";
import {
  PreviewAvatarGroup,
  PreviewMetadataLabel,
  type PreviewParticipant,
} from "./reference-preview-primitives";

/**
 * Shared stage for the field / table / team-sport scenes (football,
 * basketball, cricket, snooker, volleyball, throwball).
 *
 * These scenes lead every session card at 301×220 and still render in
 * activity posts at ~350–600px wide, so everything here is sized with
 * CONTAINER queries (`@container` on the frame) — never viewport `sm:`
 * breakpoints, which are true in a 640px gallery but false on a phone.
 *
 * Layout contract:
 * - top-center: the activity label pill;
 * - left / right: each side's players, vertically centred in the play band.
 *   A row of faces (≤ 3: two faces + "+N" for big teams) before a score, a
 *   vertical stack once the score owns the centre;
 * - centre: the score and its detail line, stacked in normal flow (no
 *   absolutely-positioned detail hanging off the score).
 */

export type FieldMatchState = "EMPTY" | "NO_SCORE" | "FULL";

type FieldMatchFrameProps = {
  label: string;
  /** Background colour/classes on the frame (behind the artwork). */
  frameClassName: string;
  matchState: FieldMatchState;
  /** The illustrated scene (absolutely positioned layers). */
  scene: ReactNode;
  yourTeam: PreviewParticipant[];
  opponentTeam: PreviewParticipant[];
  yourWinner?: boolean;
  opponentWinner?: boolean;
  linkToProfiles?: boolean;
  /** Score block for the FULL state. */
  center?: ReactNode;
  /** Props (balls etc.) layered over the scene, under the UI. */
  decor?: ReactNode;
  /**
   * The vertical band the players and score are centred in — the visible
   * floor/pitch below the stands. Defaults to just under the label pill.
   */
  bandClassName?: string;
  /** Extra classes on the anchor avatars (e.g. a sport-tinted ring). */
  avatarClassName?: string;
};

export function FieldMatchFrame({
  label,
  frameClassName,
  matchState,
  scene,
  yourTeam,
  opponentTeam,
  yourWinner,
  opponentWinner,
  linkToProfiles = false,
  center,
  decor,
  bandClassName = "top-[40px] bottom-2",
  avatarClassName,
}: FieldMatchFrameProps) {
  const stacked = matchState === "FULL";

  return (
    <div
      className={cn(
        "group @container relative isolate h-[220px] w-full overflow-hidden rounded-3xl border border-black/40 shadow-[0_8px_30px_rgba(0,0,0,0.6)] select-none",
        frameClassName,
      )}
    >
      <div className="pointer-events-none absolute inset-0 z-0">{scene}</div>
      {decor ? (
        <div className="pointer-events-none absolute inset-0 z-[5]">
          {decor}
        </div>
      ) : null}

      <PreviewMetadataLabel label={label} />

      {matchState === "EMPTY" ? <FieldWaiting /> : null}

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 z-20",
          bandClassName,
        )}
      >
        <div className="absolute inset-y-0 left-3 flex items-center @sm:left-5">
          <FieldTeamAnchor
            team={yourTeam}
            isWinner={!!yourWinner}
            isLoser={!!opponentWinner}
            stacked={stacked}
            linkToProfiles={linkToProfiles}
            avatarClassName={avatarClassName}
          />
        </div>
        {center ? (
          // A soft pool of shade so the score reads over busy markings.
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_36%_46%_at_50%_50%,rgba(0,0,0,0.42),rgba(0,0,0,0.16)_60%,transparent)]" />
        ) : null}
        {center ? (
          <div className="absolute inset-y-0 right-[54px] left-[54px] flex flex-col items-center justify-center gap-2 @sm:right-[72px] @sm:left-[72px]">
            {center}
          </div>
        ) : null}
        <div className="absolute inset-y-0 right-3 flex items-center @sm:right-5">
          <FieldTeamAnchor
            team={opponentTeam}
            isWinner={!!opponentWinner}
            isLoser={!!yourWinner}
            stacked={stacked}
            linkToProfiles={linkToProfiles}
            avatarClassName={avatarClassName}
          />
        </div>
      </div>
    </div>
  );
}

function FieldWaiting() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-[40px] bottom-2 z-10 flex items-center justify-center">
      <span className="flex items-center gap-3 rounded-full bg-black/35 px-3 py-1.5 text-[10px] font-semibold tracking-[0.4em] text-white/75 uppercase ring-1 ring-white/10">
        Waiting
      </span>
    </div>
  );
}

type FieldTeamAnchorProps = {
  team: PreviewParticipant[];
  isWinner: boolean;
  isLoser: boolean;
  stacked: boolean;
  linkToProfiles: boolean;
  avatarClassName?: string;
};

/**
 * One side's players. Up to three faces; a bigger side shows two faces and a
 * "+N" chip, so a doubles pair is never collapsed to "1 face + +1".
 */
function FieldTeamAnchor({
  team,
  isWinner,
  isLoser,
  stacked,
  linkToProfiles,
  avatarClassName,
}: FieldTeamAnchorProps) {
  if (team.length === 0) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-[30%] border border-dashed border-white/45 bg-black/20 @sm:h-12 @sm:w-12">
        <UserPlus className="h-4 w-4 text-white/70" />
      </div>
    );
  }

  const sizeClassName = stacked
    ? "h-9 w-9 @sm:h-11 @sm:w-11"
    : "h-10 w-10 @sm:h-12 @sm:w-12";

  return (
    <div
      className={cn(
        "pointer-events-auto relative transition-[opacity,filter] duration-500",
        isLoser && "opacity-80 saturate-[0.85]",
      )}
    >
      {isWinner ? (
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-3 rounded-full bg-[radial-gradient(closest-side,rgba(255,236,170,0.42),transparent)]"
        />
      ) : null}
      <PreviewAvatarGroup
        participants={team}
        maxVisible={3}
        overflowWithinMaxVisible
        linkToProfiles={linkToProfiles}
        emphasize={isWinner}
        groupClassName={cn(
          "relative justify-center",
          stacked
            ? "flex-col -space-y-2.5 space-x-0 sm:space-x-0"
            : "-space-x-2.5 sm:-space-x-2.5",
        )}
        avatarClassName={cn(
          sizeClassName,
          "border-white/30 shadow-[0_6px_14px_rgba(0,0,0,0.45)] ring-[1.5px]",
          isWinner ? "ring-[#ffe7a3]/80" : "ring-black/30",
          avatarClassName,
        )}
        overflowClassName={cn(
          sizeClassName,
          "border-white/25 bg-black/45 shadow-[0_6px_14px_rgba(0,0,0,0.45)] ring-[1.5px] ring-black/30 backdrop-blur-none",
        )}
      />
    </div>
  );
}

type FieldScoreProps = {
  transitionKey: string | number;
  left: ReactNode;
  right: ReactNode;
  leftWinner?: boolean;
  rightWinner?: boolean;
  /** Font sizing for the values (container-query classes). */
  valueClassName?: string;
  delimiter?: ReactNode;
};

const SCORE_SHADOW =
  "[text-shadow:0_2px_0_rgba(0,0,0,0.18),0_6px_18px_rgba(0,0,0,0.55)]";

export function FieldScore({
  transitionKey,
  left,
  right,
  leftWinner,
  rightWinner,
  valueClassName = "text-[54px] @xs:text-[64px] @sm:text-[78px] @md:text-[88px]",
  delimiter,
}: FieldScoreProps) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      key={transitionKey}
      initial={reduceMotion ? false : { scale: 0.94 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      className="flex items-center gap-2.5 @sm:gap-3"
    >
      <span
        className={cn(
          "leading-none font-black tracking-tight text-white/80 tabular-nums",
          SCORE_SHADOW,
          valueClassName,
          leftWinner && "text-white",
        )}
      >
        {left}
      </span>
      {delimiter ?? (
        <span className="h-[5px] w-3.5 shrink-0 rounded-full bg-white/70 shadow-[0_2px_6px_rgba(0,0,0,0.45)] @sm:w-5" />
      )}
      <span
        className={cn(
          "leading-none font-black tracking-tight text-white/80 tabular-nums",
          SCORE_SHADOW,
          valueClassName,
          rightWinner && "text-white",
        )}
      >
        {right}
      </span>
    </motion.div>
  );
}

/** Frosted pill for the line under a score (periods, sets, breaks). */
export function FieldScoreDetail({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 rounded-2xl bg-black/45 px-2.5 py-1 text-[10px] font-bold tracking-wide text-white/70 tabular-nums shadow-[0_6px_16px_rgba(0,0,0,0.35)] ring-1 ring-white/10 @sm:text-[11px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ==========================================
// ART PRIMITIVES
// ==========================================

/**
 * A floor in broadcast perspective. `top` pins the far edge (horizon) in px
 * from the frame top; the plane is `depth` px deep before tilting `tilt`°
 * towards the viewer under a `perspective` px camera; `farWidth` is the far
 * edge's width as a % of the frame.
 */
export type PlaneSpec = {
  top: number;
  depth: number;
  tilt: number;
  perspective: number;
  farWidth: number;
};

/**
 * Where a point on the plane lands on screen: `u` is 0–1 across the plane's
 * width, `v` is px into its depth. Returns a `left` % and `top` px for an
 * absolutely-positioned 2D prop, and the perspective `scale` at that depth so
 * props can grow towards the camera.
 */
export function projectOnPlane(plane: PlaneSpec, u: number, v: number) {
  const rad = (plane.tilt * Math.PI) / 180;
  const scale = plane.perspective / (plane.perspective - v * Math.sin(rad));
  return {
    left: `${50 + (u - 0.5) * plane.farWidth * scale}%`,
    top: plane.top + v * Math.cos(rad) * scale,
    scale,
  };
}

type PerspectivePlaneProps = {
  plane: PlaneSpec;
  planeClassName?: string;
  planeStyle?: CSSProperties;
  children?: ReactNode;
};

/**
 * Draw `children` flat in plane coordinates and tilt them into perspective so
 * lines converge into the distance. One composited layer — cheap however many
 * lines it holds.
 */
export function PerspectivePlane({
  plane,
  planeClassName,
  planeStyle,
  children,
}: PerspectivePlaneProps) {
  return (
    <div
      className="absolute inset-x-0 bottom-0"
      style={{
        top: plane.top,
        perspective: `${plane.perspective}px`,
        perspectiveOrigin: "50% 0%",
      }}
    >
      <div
        className={cn("absolute top-0 origin-top", planeClassName)}
        style={{
          left: `${(100 - plane.farWidth) / 2}%`,
          width: `${plane.farWidth}%`,
          height: plane.depth,
          transform: `rotateX(${plane.tilt}deg)`,
          ...planeStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A packed crowd in tiered stands. Pure gradients: dots for heads, a few
 * colour layers for shirts and scarves, tier shadows, and an LED ad board.
 */
export function StadiumStands({
  className,
  baseClassName = "bg-[linear-gradient(180deg,#05080c_0%,#0c1416_100%)]",
  crowdOpacity = 0.75,
  crowdA = "255,255,255",
  crowdB = "255,90,90",
  boardClassName,
}: {
  className?: string;
  baseClassName?: string;
  crowdOpacity?: number;
  /** RGB triplets for the two shirt colours in the crowd. */
  crowdA?: string;
  crowdB?: string;
  /** Ad-board strip along the bottom edge; omit for none. */
  boardClassName?: string;
}) {
  return (
    <div className={cn("absolute inset-x-0 top-0 overflow-hidden", className)}>
      <div className={cn("absolute inset-0", baseClassName)} />
      <div
        className="absolute inset-0"
        style={{
          opacity: crowdOpacity,
          ["--crowd-a" as string]: crowdA,
          ["--crowd-b" as string]: crowdB,
          backgroundImage: [
            // heads, lit from the pitch
            "radial-gradient(circle at 50% 40%, rgba(255,232,210,0.34) 0 0.75px, transparent 1.2px)",
            // shirts and scarves
            "radial-gradient(ellipse at 50% 60%, rgba(var(--crowd-a,255,255,255),0.32) 0 1px, transparent 1.5px)",
            "radial-gradient(ellipse at 50% 60%, rgba(var(--crowd-b,255,90,90),0.34) 0 1px, transparent 1.5px)",
          ].join(","),
          backgroundSize: "3.5px 4px, 7px 4px, 10.5px 4px",
          backgroundPosition: "0 0, 1.75px 1.5px, 5px 1.5px",
          maskImage:
            "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 30%, #000 75%)",
          WebkitMaskImage:
            "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 30%, #000 75%)",
        }}
      />
      {/* Tier shadows — horizontal rows of seating. */}
      <div className="absolute inset-0 bg-[repeating-linear-gradient(180deg,transparent_0px,transparent_7px,rgba(0,0,0,0.5)_7px,rgba(0,0,0,0.5)_8.5px)]" />
      {/* Roof overhang with a lit fascia */}
      <div className="absolute inset-x-0 top-0 h-[13px] bg-[linear-gradient(180deg,#020304,#0a0f12)] shadow-[0_1px_0_rgba(200,220,235,0.18)]" />
      {boardClassName ? (
        <div
          className={cn("absolute inset-x-0 bottom-0 h-[6px]", boardClassName)}
        />
      ) : null}
    </div>
  );
}

/**
 * A floodlight bank with its bloom and a soft beam reaching down into the
 * scene. `side` mirrors it; `x`/`y` place the lamp head.
 */
export function Floodlight({
  side,
  x,
  y,
  beamTo = "50%",
  beamOpacity = 0.16,
  color = "220,236,255",
  pylon = false,
}: {
  side: "left" | "right";
  x: string;
  y: number;
  /** Horizontal % where the beam lands at the bottom of the frame. */
  beamTo?: string;
  beamOpacity?: number;
  /** RGB triplet for the light's tint. */
  color?: string;
  /** Draw a lattice pylon under the lamp head. */
  pylon?: boolean;
}) {
  const head: CSSProperties =
    side === "left" ? { left: x, top: y } : { right: x, top: y };
  return (
    <>
      {/* Beam */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, rgba(${color},${beamOpacity}) 0%, rgba(${color},0) 92%)`,
          clipPath:
            side === "left"
              ? `polygon(calc(${x} + 2px) ${y + 4}px, calc(${x} + 22px) ${y + 4}px, calc(${beamTo} + 18%) 100%, calc(${beamTo} - 18%) 100%)`
              : `polygon(calc(100% - ${x} - 22px) ${y + 4}px, calc(100% - ${x} - 2px) ${y + 4}px, calc(100% - ${beamTo} + 18%) 100%, calc(100% - ${beamTo} - 18%) 100%)`,
        }}
      />
      {/* Bloom */}
      <div
        className="absolute h-[90px] w-[90px] rounded-full"
        style={{
          ...(side === "left"
            ? { left: `calc(${x} - 34px)` }
            : { right: `calc(${x} - 34px)` }),
          top: y - 40,
          background: `radial-gradient(closest-side, rgba(${color},0.55), rgba(${color},0.12) 45%, transparent)`,
        }}
      />
      {pylon ? (
        <div
          className="absolute w-[3px] bg-[linear-gradient(180deg,#2a3138,#0d1114)]"
          style={{
            ...(side === "left"
              ? { left: `calc(${x} + 10px)` }
              : { right: `calc(${x} + 10px)` }),
            top: y + 8,
            height: 60,
          }}
        />
      ) : null}
      {/* Lamp head: a grid of lit cells */}
      <div
        className="absolute h-[9px] w-[24px] rounded-[2px] bg-[#f4f9ff]"
        style={{
          ...head,
          backgroundImage:
            "linear-gradient(90deg, rgba(40,50,60,0.6) 1px, transparent 1px), linear-gradient(180deg, rgba(40,50,60,0.6) 1px, transparent 1px)",
          backgroundSize: "6px 4.5px",
          boxShadow: `0 0 6px 2px rgba(${color},0.9), 0 0 18px 6px rgba(${color},0.35)`,
        }}
      />
    </>
  );
}

/** A gentle hover for the one "hero" ball a scene shows; off under reduced motion. */
export function FloatingProp({
  className,
  children,
  amplitude = 3,
  duration = 4.5,
}: {
  className?: string;
  children: ReactNode;
  amplitude?: number;
  duration?: number;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      animate={reduceMotion ? undefined : { y: [0, -amplitude, 0] }}
      transition={
        reduceMotion
          ? undefined
          : {
              duration,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }
      }
    >
      {children}
    </motion.div>
  );
}

/** Soft elliptical contact shadow under a prop. */
export function ContactShadow({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "absolute rounded-[50%] bg-[radial-gradient(closest-side,rgba(0,0,0,0.55),transparent)]",
        className,
      )}
      style={style}
    />
  );
}

export type { PreviewParticipant };

type CourtNetProps = {
  plane: PlaneSpec;
  /** Post positions, px into the plane (far and near). */
  farV: number;
  nearV: number;
  /** Net height (tape above the floor) and mesh drop, in plane px. */
  height: number;
  drop: number;
  /** Fake camera offset so the net reads as a surface, not a line. */
  skew?: number;
  postClassName?: string;
  tapeColor?: string;
  meshColor?: string;
};

/**
 * A net strung across the middle of a perspective court: two posts (the near
 * one larger) and a mesh band whose tape falls from the far post-top to the
 * near one. One small SVG centred on the frame — never wider than 60px, so it
 * can't spill past the frame edges, and both posts are kept inside its height.
 */
export function CourtNet({
  plane,
  farV,
  nearV,
  height,
  drop,
  skew = 18,
  postClassName = "fill-[#d9dde2]",
  tapeColor = "rgba(255,255,255,0.95)",
  meshColor = "rgba(255,255,255,0.4)",
}: CourtNetProps) {
  const id = useId().replace(/:/g, "");
  const far = projectOnPlane(plane, 0.5, farV);
  const near = projectOnPlane(plane, 0.5, nearV);
  const cx = 30;
  const farX = cx + skew / 2;
  const nearX = cx - skew / 2;
  const farTop = far.top - height * far.scale;
  const nearTop = near.top - height * near.scale;
  const farBottom = farTop + drop * far.scale;
  const nearBottom = nearTop + drop * near.scale;
  const postW = (s: number) => 2.4 * s;
  return (
    <svg
      aria-hidden
      className="absolute top-0 left-1/2 h-[220px] w-[60px] -translate-x-1/2 overflow-visible"
      viewBox="0 0 60 220"
    >
      <defs>
        <pattern
          id={`mesh-${id}`}
          width="3"
          height="3"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <path d="M0 0 L0 3 M0 0 L3 0" stroke={meshColor} strokeWidth="0.75" />
        </pattern>
      </defs>
      {/* Shadows the posts throw across the floor */}
      <path
        d={`M${farX} ${far.top} L${farX - 16} ${far.top + 6}`}
        stroke="rgba(0,0,0,0.28)"
        strokeWidth={postW(far.scale)}
        strokeLinecap="round"
      />
      <path
        d={`M${nearX} ${near.top} L${nearX - 26} ${near.top + 9}`}
        stroke="rgba(0,0,0,0.3)"
        strokeWidth={postW(near.scale)}
        strokeLinecap="round"
      />
      {/* Mesh */}
      <polygon
        points={`${farX},${farTop} ${nearX},${nearTop} ${nearX},${nearBottom} ${farX},${farBottom}`}
        fill={`url(#mesh-${id})`}
      />
      <polygon
        points={`${farX},${farTop} ${nearX},${nearTop} ${nearX},${nearBottom} ${farX},${farBottom}`}
        fill="rgba(255,255,255,0.07)"
      />
      {/* Tape top and bottom cord */}
      <line
        x1={farX}
        y1={farTop}
        x2={nearX}
        y2={nearTop}
        stroke={tapeColor}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <line
        x1={farX}
        y1={farBottom}
        x2={nearX}
        y2={nearBottom}
        stroke={tapeColor}
        strokeOpacity={0.55}
        strokeWidth={1}
      />
      {/* Posts */}
      <rect
        x={farX - postW(far.scale) / 2}
        y={farTop - 2}
        width={postW(far.scale)}
        height={far.top - farTop + 2}
        rx={1}
        className={postClassName}
      />
      <rect
        x={nearX - postW(near.scale) / 2}
        y={nearTop - 3}
        width={postW(near.scale)}
        height={near.top - nearTop + 3}
        rx={1.2}
        className={postClassName}
      />
    </svg>
  );
}
